/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import confetti from 'canvas-confetti';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Trophy, Target, Flame, PlayCircle, CheckCircle2, BookOpen,
  Calendar, Sparkles, GraduationCap, Clock, Crown,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Convert a YouTube/Vimeo URL → an iframe-embeddable URL. We NEVER
 * host video bytes ourselves (Phase 321 directive); always embed.
 */
const toEmbedUrl = (url) => {
  if (!url) return null;
  // youtu.be/abc → youtube.com/embed/abc
  const ytShort = url.match(/youtu\.be\/([\w-]+)/);
  if (ytShort) return `https://www.youtube.com/embed/${ytShort[1]}`;
  // youtube.com/watch?v=abc → youtube.com/embed/abc
  const ytWatch = url.match(/youtube\.com\/watch\?v=([\w-]+)/);
  if (ytWatch) return `https://www.youtube.com/embed/${ytWatch[1]}`;
  // already an /embed/ link → keep
  if (url.includes('/embed/')) return url;
  // vimeo.com/12345 → player.vimeo.com/video/12345
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};

const skillBadge = {
  beginner: { color: 'bg-emerald-100 text-emerald-700 border-emerald-300', label: 'Beginner' },
  intermediate: { color: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Intermediate' },
  advanced: { color: 'bg-rose-100 text-rose-700 border-rose-300', label: 'Advanced' },
};

const relativeTime = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
};

const fireConfetti = () => {
  // Two-burst confetti for max dopamine. Brand-aligned palette.
  const colors = ['#10b981', '#059669', '#fbbf24', '#f59e0b', '#0f766e'];
  confetti({
    particleCount: 90, spread: 70, origin: { y: 0.6 }, colors,
  });
  setTimeout(() => {
    confetti({
      particleCount: 50, spread: 100, origin: { y: 0.6 }, colors,
      shapes: ['circle'],
    });
  }, 220);
};

const ScorecardCard = ({ card }) => {
  const score = card.score;
  const scoreColor = score == null
    ? 'bg-slate-100 text-slate-600'
    : score >= 8 ? 'bg-emerald-500 text-white'
      : score >= 5 ? 'bg-amber-500 text-white'
        : 'bg-rose-500 text-white';
  return (
    <div
      className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:border-emerald-300 transition-colors bg-white"
      data-testid={`scorecard-${card.id}`}
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-extrabold ${scoreColor} shrink-0`}>
        {score != null ? `${score}/10` : '—'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">
          {card.batch_name || 'Practice session'}
        </p>
        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
          <Clock className="h-3 w-3" /> {relativeTime(card.created_at)}
        </p>
        {card.comments && (
          <p className="text-xs text-slate-600 italic mt-1.5 line-clamp-2">
            "{card.comments}"
          </p>
        )}
      </div>
    </div>
  );
};

const HomeworkCard = ({ assignment, onComplete, completing }) => {
  const embed = toEmbedUrl(assignment.drill_video_url);
  const skill = (assignment.drill_skill_level || '').toLowerCase();
  const SBadge = skillBadge[skill] || skillBadge.intermediate;
  const isCompleted = assignment.status === 'completed';
  return (
    <Card
      className={`border-2 ${isCompleted ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200'} overflow-hidden`}
      data-testid={`homework-card-${assignment.id}`}
    >
      {embed && (
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            src={embed}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={assignment.drill_title}
            data-testid={`drill-iframe-${assignment.id}`}
          />
        </div>
      )}
      {!embed && (
        <div
          className="w-full h-32 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center"
          data-testid={`drill-no-video-${assignment.id}`}
        >
          <PlayCircle className="h-10 w-10 text-slate-400" />
        </div>
      )}
      <CardContent className="p-4">
        <div className="flex items-start gap-2 mb-2">
          <Badge className={`${SBadge.color} text-xs`}>
            {SBadge.label}
          </Badge>
          {isCompleted && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-xs">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Completed
            </Badge>
          )}
        </div>
        <h3 className="font-bold text-base text-slate-900 leading-tight" data-testid={`drill-title-${assignment.id}`}>
          {assignment.drill_title}
        </h3>
        {(assignment.drill_tags?.length || 0) > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {assignment.drill_tags.slice(0, 4).map((tag, idx) => (
              <Badge key={idx} variant="outline" className="text-[10px] py-0 px-1.5">
                {tag}
              </Badge>
            ))}
          </div>
        )}
        {assignment.note_excerpt && (
          <p className="text-xs text-slate-500 italic mt-2 line-clamp-2">
            Coach: "{assignment.note_excerpt}"
          </p>
        )}
        <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          Assigned {relativeTime(assignment.assigned_at)}
        </p>
        {!isCompleted && (
          <Button
            className="w-full mt-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
            onClick={() => onComplete(assignment.id)}
            disabled={completing === assignment.id}
            data-testid={`mark-complete-btn-${assignment.id}`}
          >
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            {completing === assignment.id ? 'Saving...' : 'Mark as Completed'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default function DigitalLocker() {
  const [locker, setLocker] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  const loadLocker = useCallback(async () => {
    try {
      const { data } = await api.get('/student/locker');
      setLocker(data);
    } catch (err) {
      if (err.response?.status === 404) {
        setLocker({ error: 'no_linked_student' });
      } else {
        toast.error('Failed to load locker');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLeaderboard = useCallback(async () => {
    try {
      const { data } = await api.get('/academy/leaderboard?window_days=7&limit=5');
      setLeaderboard(data);
    } catch (err) {
      // Silent — leaderboard is supplementary; locker still works without it.
    }
  }, []);

  useEffect(() => {
    loadLocker();
    loadLeaderboard();
  }, [loadLocker, loadLeaderboard]);

  const handleComplete = async (assignmentId) => {
    setCompleting(assignmentId);
    try {
      const { data } = await api.post(`/student/drill-assignments/${assignmentId}/complete`);
      if (data.already_completed) {
        toast.info('Already marked complete!');
      } else {
        fireConfetti();
        toast.success("Crushed it! 🔥");
      }
      // Refresh both panels.
      await Promise.all([loadLocker(), loadLeaderboard()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark complete');
    } finally {
      setCompleting(null);
    }
  };

  if (loading) {
    return (
      <div className="p-4 space-y-4 max-w-4xl mx-auto">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!locker || locker.error) {
    return (
      <div className="p-8 text-center max-w-md mx-auto" data-testid="locker-empty-state">
        <GraduationCap className="h-12 w-12 mx-auto text-slate-300 mb-3" />
        <h2 className="text-xl font-bold text-slate-900">Digital Locker</h2>
        <p className="text-sm text-slate-500 mt-2">
          We couldn't find a student profile linked to your account. Reach out to your academy to get connected.
        </p>
      </div>
    );
  }

  const cabinet = locker.trophy_cabinet;
  const homework = locker.coachs_homework || [];
  const pendingHomework = homework.filter((h) => h.status !== 'completed');
  const completedHomework = homework.filter((h) => h.status === 'completed');

  return (
    <div className="p-3 sm:p-5 max-w-4xl mx-auto space-y-5" data-testid="digital-locker">
      {/* Hero */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 rounded-2xl p-5 sm:p-6 text-white shadow-lg">
        <div className="flex items-start gap-3">
          <div className="p-2.5 bg-white/20 rounded-xl">
            <GraduationCap className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-extrabold leading-tight">
              {locker.student?.name || 'Student'}'s Locker
            </h1>
            <p className="text-emerald-100 text-sm mt-0.5">
              {locker.student?.sport && `${locker.student.sport} · `}{locker.student?.batch || 'Your training base'}
            </p>
          </div>
        </div>
      </div>

      {/* Weekly Leaderboard (Phase 322) */}
      {leaderboard && (leaderboard.leaderboard?.length || 0) > 0 && (
        <section data-testid="weekly-leaderboard">
          <h2 className="text-base font-bold text-slate-900 mb-2.5 flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-500" />
            Weekly Leaderboard
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-amber-300 text-amber-700">
              Last {leaderboard.window_days || 7} days
            </Badge>
          </h2>
          <Card className="overflow-hidden border-amber-200 bg-gradient-to-br from-amber-50/40 to-emerald-50/40">
            <CardContent className="p-3 space-y-1.5">
              {leaderboard.leaderboard.map((row) => (
                <div
                  key={row.student_id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    row.is_current_user
                      ? 'bg-emerald-100 border-2 border-emerald-400 shadow-sm'
                      : 'bg-white border border-slate-200'
                  }`}
                  data-testid={`leaderboard-row-${row.rank}`}
                >
                  <span className="text-lg w-8 text-center shrink-0" data-testid={`leaderboard-medal-${row.rank}`}>
                    {row.medal || <span className="text-xs font-bold text-slate-400">#{row.rank}</span>}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${row.is_current_user ? 'text-emerald-900' : 'text-slate-900'}`}>
                      {row.display_name}
                      {row.is_current_user && (
                        <span className="ml-1.5 text-[10px] uppercase font-bold text-emerald-700">
                          you
                        </span>
                      )}
                    </p>
                    {row.batch && (
                      <p className="text-[11px] text-slate-500 truncate">{row.batch}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-extrabold text-slate-900 leading-tight">
                      {row.drills_completed}
                      <span className="text-[10px] font-medium text-slate-500 ml-1">drills</span>
                    </p>
                    {row.attendance_streak_days > 0 && (
                      <p className="text-[10px] text-rose-600 font-medium flex items-center justify-end gap-0.5">
                        <Flame className="h-2.5 w-2.5" />
                        {row.attendance_streak_days}d streak
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {leaderboard.my_rank && leaderboard.my_rank > 5 && (
                <div
                  className="mt-2 px-3 py-2 rounded-lg bg-slate-100 text-center text-xs text-slate-600"
                  data-testid="leaderboard-my-position"
                >
                  You're <strong>#{leaderboard.my_rank}</strong> —{' '}
                  {leaderboard.my_completion_delta_to_podium
                    ? `${leaderboard.my_completion_delta_to_podium} drill${leaderboard.my_completion_delta_to_podium === 1 ? '' : 's'} away from the podium!`
                    : 'keep climbing!'}
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      )}

      {/* Trophy Cabinet stats */}
      <section data-testid="trophy-cabinet">
        <h2 className="text-base font-bold text-slate-900 mb-2.5 flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-500" />
          Trophy Cabinet
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-3.5 text-center">
              <div className="text-2xl font-extrabold text-amber-700" data-testid="streak-value">
                {cabinet.attendance_streak_days}
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5 flex items-center justify-center gap-1">
                <Flame className="h-3 w-3 text-rose-500" />
                Day streak
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-3.5 text-center">
              <div className="text-2xl font-extrabold text-emerald-700" data-testid="completed-count">
                {cabinet.completed_drills_count}
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5">Drills completed</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3.5 text-center">
              <div className="text-2xl font-extrabold text-slate-900" data-testid="assigned-count">
                {cabinet.total_assigned_drills}
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5">Total assigned</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3.5 text-center">
              <div className="text-2xl font-extrabold text-slate-900" data-testid="scorecards-count">
                {cabinet.recent_scorecards?.length || 0}
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5">Recent scorecards</div>
            </CardContent>
          </Card>
        </div>
        {(cabinet.recent_scorecards?.length || 0) > 0 && (
          <Card className="mt-3">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
                Latest Coach Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              {cabinet.recent_scorecards.slice(0, 3).map((card) => (
                <ScorecardCard key={card.id} card={card} />
              ))}
            </CardContent>
          </Card>
        )}
      </section>

      {/* Coach's Homework */}
      <section data-testid="coachs-homework">
        <h2 className="text-base font-bold text-slate-900 mb-2.5 flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-emerald-600" />
          Coach's Homework
          {pendingHomework.length > 0 && (
            <Badge className="bg-rose-500 text-white text-[10px] px-1.5">
              {pendingHomework.length} pending
            </Badge>
          )}
        </h2>
        {homework.length === 0 ? (
          <Card data-testid="no-homework">
            <CardContent className="p-8 text-center">
              <Target className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm font-semibold text-slate-700">No homework yet</p>
              <p className="text-xs text-slate-500 mt-1">
                Your coach will assign drills here based on your practice sessions.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pendingHomework.map((a) => (
              <HomeworkCard
                key={a.id}
                assignment={a}
                onComplete={handleComplete}
                completing={completing}
              />
            ))}
            {completedHomework.map((a) => (
              <HomeworkCard
                key={a.id}
                assignment={a}
                onComplete={handleComplete}
                completing={completing}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
